/**
 * Care Pillar Selector Widget
 *
 * Displays system-suggested pillars and lets the user enable/disable them.
 * Used in the Care Assessment CoPilot Guide flow.
 */

import type { WidgetProps } from '@tonnie/copilot';
import { useTranslation } from '@tonnie/locale';
import { Button, Card, CardContent, cn } from '@tonnie/ui';
import { Check } from '@tonnie/assets';
import { useCallback } from 'react';
import { useWidgetState } from '@/features/platform/copilot/flow-lifecycle';

interface PillarOption {
  id: string;
  nameKey: string;
  descriptionKey: string;
  icon: string;
  recommended: boolean;
  conditional?: boolean;
  conditionType?: string;
}

// Default pillars. Every tile wears the Care rose — the selection state, not a
// per-pillar hue, is what the tile shows.
const DEFAULT_PILLARS: PillarOption[] = [
  {
    id: 'housing_stability',
    nameKey: 'care.pillar.housing_stability.name',
    descriptionKey: 'care.pillar.housing_stability.desc',
    icon: '🏠',
    recommended: true,
  },
  {
    id: 'schooling_childcare',
    nameKey: 'care.pillar.schooling_childcare.name',
    descriptionKey: 'care.pillar.schooling_childcare.desc',
    icon: '🎓',
    recommended: true,
    conditional: true,
    conditionType: 'has_dependents',
  },
  {
    id: 'work_fit',
    nameKey: 'care.pillar.work_fit.name',
    descriptionKey: 'care.pillar.work_fit.desc',
    icon: '💼',
    recommended: true,
  },
  {
    id: 'wellbeing_social',
    nameKey: 'care.pillar.wellbeing_social.name',
    descriptionKey: 'care.pillar.wellbeing_social.desc',
    icon: '😊',
    recommended: true,
  },
];

interface PillarSelectorConfig {
  title?: string;
  description?: string;
  pillars?: PillarOption[];
  variableName?: string;
}

type PillarSelectorWidgetProps = WidgetProps & { config?: PillarSelectorConfig };

const DEFAULTS_BY_ID = new Map(DEFAULT_PILLARS.map((p) => [p.id, p]));

export function CarePillarSelectorWidget({ config, onComplete }: PillarSelectorWidgetProps) {
  const { t } = useTranslation();

  const pillars = (config?.pillars ?? DEFAULT_PILLARS).map((p) => {
    const defaults = DEFAULTS_BY_ID.get(p.id);
    if (!defaults) return p;
    return {
      ...defaults,
      ...p,
      nameKey: defaults.nameKey,
      descriptionKey: defaults.descriptionKey,
      icon: defaults.icon,
    };
  });

  // Persist the selection so a mid-flow refresh doesn't reset it (parity with
  // the check-in preferences widget). Defaults to the recommended pillars.
  const [persisted, patchPersisted] = useWidgetState<{ selected: string[] }>(
    'care_pillar_selector',
    { selected: pillars.filter((p) => p.recommended).map((p) => p.id) }
  );
  const activePillars = new Set(persisted.selected);

  const togglePillar = useCallback(
    (id: string) => {
      const next = new Set(persisted.selected);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      patchPersisted({ selected: [...next] });
    },
    [persisted.selected, patchPersisted]
  );

  const handleSubmit = useCallback(() => {
    const varName = config?.variableName ?? 'activePillars';
    onComplete?.({
      [varName]: Array.from(activePillars),
    });
  }, [activePillars, config, onComplete]);

  return (
    <Card className="border-tone-rose-line" data-testid="widget-care_pillar_selector">
      <CardContent className="pt-6 space-y-4">
        <div>
          <h3 className="text-sm font-semibold mb-1">
            {config?.title ?? t('care.pillarSelector.title')}
          </h3>
          <p className="text-xs text-muted-foreground">
            {config?.description ?? t('care.pillarSelector.desc')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {pillars.map((pillar) => {
            const isActive = activePillars.has(pillar.id);
            return (
              <button
                key={pillar.id}
                onClick={() => togglePillar(pillar.id)}
                aria-pressed={isActive}
                data-testid={`widget-care_pillar_selector-pillar-${pillar.id}`}
                className={cn(
                  'relative p-4 rounded-xl border-2 text-left transition-all',
                  isActive
                    ? 'border-tone-rose-solid bg-tone-rose-subtle'
                    : 'border-border hover:border-tone-rose-line'
                )}
              >
                {isActive && (
                  <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-tone-rose-solid flex items-center justify-center">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xl">{pillar.icon}</span>
                  <span className="text-sm font-semibold">{t(pillar.nameKey)}</span>
                </div>
                <p className="text-xs text-muted-foreground line-clamp-2">
                  {t(pillar.descriptionKey, '')}
                </p>
                {pillar.conditional && (
                  <span className="inline-block mt-2 text-2xs px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                    {t('care.pillarSelector.conditional')}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <Button
          onClick={handleSubmit}
          disabled={activePillars.size === 0}
          data-testid="widget-care_pillar_selector-confirm"
          className="w-full bg-tone-rose-solid hover:bg-tone-rose-solid-hover text-tone-rose-on-solid"
        >
          {t('common.continue')} ({activePillars.size} {t('care.pillarSelector.selected')})
        </Button>
      </CardContent>
    </Card>
  );
}
